import React, { useCallback, useEffect, useState } from 'react';
import { TokensIcon } from '@radix-ui/react-icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { apiClient, API_ENDPOINTS } from '../../lib';
import { useAuthStore } from '../../stores/authStore';
import { isAdminRole } from '../../lib/roles';
import { useSSE } from '../../hooks/useSSE';
import {
  POINTS_ALLOWANCE,
  LOW_POINTS_THRESHOLD,
  LOW_POINTS_WARNING_STORAGE_KEY,
} from '../../constants/points';

interface BalanceResponse {
  points: number;
}

function getWarningStorageKey(userId: number): string {
  return `${LOW_POINTS_WARNING_STORAGE_KEY}:${POINTS_ALLOWANCE}:${userId}`;
}

const LowPointsWarning: React.FC = () => {
  const { token, user } = useAuthStore();
  const [points, setPoints] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const refreshBalance = useCallback(async () => {
    if (!token || !user || isAdminRole(user.role)) return;

    try {
      const { data } = await apiClient.get<BalanceResponse>(API_ENDPOINTS.points.balance, token);
      const balance = Number(data.points);
      setPoints(balance);

      const storageKey = getWarningStorageKey(user.id);
      if (balance >= LOW_POINTS_THRESHOLD) {
        localStorage.removeItem(storageKey);
        return;
      }

      if (!localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, 'shown');
        setOpen(true);
      }
    } catch (error) {
      // Balance is supplementary UI; an API failure should not interrupt the current page.
      console.error('[Points] Failed to refresh balance:', error);
    }
  }, [token, user]);

  useEffect(() => {
    setPoints(null);
    setOpen(false);
    void refreshBalance();

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshBalance();
      }
    }, 30_000);

    return () => window.clearInterval(timer);
  }, [refreshBalance]);

  useSSE({
    onTaskLifecycle: (_taskId, data) => {
      if (
        data.status === 'queued'
        || data.status === 'completed'
        || data.status === 'failed'
        || data.status === 'cancelled'
      ) {
        void refreshBalance();
      }
    },
  });

  if (!user || isAdminRole(user.role) || points === null) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="z-[300] max-w-md">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
            <TokensIcon className="h-5 w-5" />
          </div>
          <DialogTitle>积分余量不足</DialogTitle>
          <DialogDescription>
            当前可用算力剩余20%，请合理使用。如有问题，请及时联系导师处理。
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm">
          <span className="text-content-muted">当前积分：</span>
          <strong className="ml-1 tabular-nums text-amber-600">{points}</strong>
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => setOpen(false)}>
            我知道了
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LowPointsWarning;
